import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useClient, PLATFORM_LABEL } from '../ClientContext';
import { apiGet, apiPost, AuthError } from '../api';
import Icon from '../components/Icon';
import AgentSteps from '../components/AgentSteps';
import PlatformSwitch from '../components/PlatformSwitch';
import './reporting/reporting.css';

/**
 * Campaign Build.
 *
 * The composer is the page. It used to be a 13.5px input in a strip at the
 * bottom of a scrolling pane, which made everything else on the screen louder
 * than the one thing the page exists to do.
 *
 * Scope lives in the composer as chips. They compose the message rather than
 * adding parameters, so the endpoint contract is unchanged — and whatever the
 * chips add is shown verbatim in the thread, so nothing is sent that the user
 * cannot see.
 */

const OBJECTIVES = ['Leads', 'Traffic', 'Awareness', 'Sales'];

const QUICK = [
  { colour: 'var(--pine-light)', text: 'Build a leads campaign at £200 a day using my hero image' },
  { colour: 'var(--brass)',      text: 'Create an awareness campaign for 500,000 people' },
  { colour: 'var(--info)',       text: 'What creative do I have available?' },
  { colour: 'var(--bad)',        text: 'What rules apply to this client?' },
];

// Google Search starting points. No creative, no objective: a Search
// campaign is keywords, a bid, a landing page and text the assistant writes.
const QUICK_GOOGLE = [
  { colour: 'var(--pine-light)', text: 'Build a brand Search campaign at £10 a day, manual CPC at £1, UK, landing on our homepage' },
  { colour: 'var(--info)',       text: 'Which match types and bidding strategies am I allowed to use?' },
  { colour: 'var(--bad)',        text: 'What rules apply to this account?' },
];

// Where each platform's campaigns are switched on, for the paused notice.
const ACTIVATE_IN = { meta: 'Ads Manager', google: 'Google Ads' };

const TOPICS = [
  { title: 'Lead generation',  blurb: 'Form fills and calls from local search intent' },
  { title: 'Retargeting',      blurb: 'Re-reach people who viewed but did not convert' },
  { title: 'Launch awareness', blurb: 'Broad reach for a new product or location' },
  { title: 'Budget pacing',    blurb: 'Spread spend evenly across the month' },
];

const money = (pence) =>
  `£${((pence || 0) / 100).toLocaleString('en-GB', { maximumFractionDigits: 2 })}`;

const OUTCOME = {
  APPROVED: { cls: 'approved', label: 'Approved' },
  FLAG:     { cls: 'flagged',  label: 'Flagged for review' },
  BLOCK:    { cls: 'blocked',  label: 'Blocked' },
  OBSERVE:  { cls: 'observe',  label: 'Observe' },
  // Google only: the proposal could never have been built, so it went back to
  // the assistant to fix. Not a governance decision, and not recorded as one.
  invalid:  { cls: 'flagged',  label: 'Sent back to fix' },
};

// The build tools whose result is a decision card, newest last.
const BUILD_TOOLS = ['create_campaign', 'create_search_campaign'];

// Keyed by client, so each client's thread is its own. Bounded so a long
// thread cannot fill the browser's storage quota and start failing silently.
const storageKey = (clientId) => `bidsense.campaignBuild.${clientId}`;
const MAX_STORED_MESSAGES = 40;

function loadThread(clientId) {
  if (!clientId || clientId === 'ALL') return [];
  try {
    const raw = localStorage.getItem(storageKey(clientId));
    const parsed = raw ? JSON.parse(raw) : [];
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

function saveThread(clientId, messages) {
  if (!clientId || clientId === 'ALL') return;
  try {
    localStorage.setItem(
      storageKey(clientId),
      JSON.stringify(messages.slice(-MAX_STORED_MESSAGES))
    );
  } catch {
    // Quota exceeded or storage disabled. The thread still works for this
    // session; it just will not survive navigation.
  }
}

/** A scope selector that sits inside the composer. */
function Chip({ icon, label, options, value, onPick, disabled, warn }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    const onDown = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    const onKey = (e) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  if (!options) {
    return (
      <span className="chip" style={{ cursor: 'default' }}>
        <Icon name={icon} size={13} />
        <span>{label}</span>
      </span>
    );
  }

  return (
    <div className={`chip ${open ? 'open' : ''}`} ref={ref}>
      <Icon name={icon} size={13} />
      <button
        type="button"
        onClick={() => !disabled && setOpen((v) => !v)}
        disabled={disabled}
        aria-haspopup="listbox"
        aria-expanded={open}
        style={{
          background: 'none', border: 'none', padding: 0, cursor: disabled ? 'default' : 'pointer',
          font: 'inherit', color: 'inherit', display: 'inline-flex', alignItems: 'center', gap: 6,
        }}
      >
        <span>{label}</span>
        {warn && <span className="warn-dot" />}
        <Icon name="down" size={12} strokeWidth={2.6} />
      </button>

      {open && (
        <div className="chip-menu" role="listbox">
          {options.map((opt) => (
            <button
              type="button"
              key={opt.value ?? '__any'}
              role="option"
              aria-selected={opt.value === value}
              className={`chip-opt ${opt.value === value ? 'sel' : ''}`}
              onClick={() => { setOpen(false); onPick(opt.value); }}
            >
              <span className="o-main">
                {opt.label}
                {opt.sub && <span className="o-sub">{opt.sub}</span>}
              </span>
              {opt.flag && (
                <span className={`pill ${opt.flag.cls}`} style={{ fontSize: 9, padding: '2px 6px' }}>
                  {opt.flag.text}
                </span>
              )}
              <span className="o-tick"><Icon name="check" size={14} strokeWidth={2.6} /></span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

/**
 * The governance decision, as a record rather than a sentence. A buyer should
 * be able to read the outcome, the rule and the action id without taking the
 * model's account of its own work on trust.
 */
function DecisionCard({ result, args }) {
  if (!result || !result.outcome) return null;
  const style = OUTCOME[result.outcome] || { cls: '', label: result.outcome };
  const created = result.created && result.campaign_id;
  const google = result.platform === 'google';
  const platformName = google ? 'Google Ads' : 'Meta';

  return (
    <div className={`decision ${style.cls}`}>
      <div className="dec-head">
        <span className={`pill ${style.cls}`}>
          <Icon name={result.outcome === 'APPROVED' ? 'check' : 'alert'} size={12} strokeWidth={2.6} />
          {style.label}
        </span>
        {result.rules_evaluated != null && (
          <span className="meta">{result.rules_evaluated} rules evaluated</span>
        )}
      </div>

      <div className="dec-spec">
        <span><span className="k">Campaign</span><span className="v">{args?.name || '—'}</span></span>
        {google ? (
          <>
            <span><span className="k">Bidding</span><span className="v">{args?.bidding_strategy || '—'}</span></span>
            <span><span className="k">Keywords</span><span className="v">{(args?.keywords || []).length}</span></span>
            <span><span className="k">Final URL</span><span className="v">{args?.final_url || '—'}</span></span>
          </>
        ) : (
          <span><span className="k">Objective</span><span className="v">{args?.objective || '—'}</span></span>
        )}
        <span><span className="k">Daily budget</span><span className="v">{money(args?.daily_budget_pence)}</span></span>
        {args?.estimated_audience_size != null && (
          <span><span className="k">Audience</span><span className="v">{Number(args.estimated_audience_size).toLocaleString('en-GB')}</span></span>
        )}
        {args?.creative_name && (
          <span><span className="k">Creative</span><span className="v">{args.creative_name}</span></span>
        )}
      </div>

      {result.outcome === 'invalid' && (result.problems || []).length > 0 && (
        <div className="dec-reason warn">
          <Icon name="alert" size={15} strokeWidth={2.2} />
          <span>{result.problems.join('; ')}</span>
        </div>
      )}

      {result.reason && (
        <div className={`dec-reason ${result.outcome === 'BLOCK' ? 'bad' : 'warn'}`}>
          <Icon name="alert" size={15} strokeWidth={2.2} />
          <span>
            {result.rule_triggered && <span className="tag-mono" style={{ marginRight: 8 }}>{result.rule_triggered}</span>}
            {result.reason}
          </span>
        </div>
      )}

      {created && (
        <div className="dec-created">
          Created on {platformName} and <strong>paused</strong>.{' '}
          {google ? 'Enable it in Google Ads when you are ready.' : 'Activate it in Ads Manager when you are ready.'}
          <div className="dec-ids">
            <span className="tag-mono">Campaign {result.campaign_id}</span>
            {result.adset_id && <span className="tag-mono">Ad set {result.adset_id}</span>}
            {result.ad_group_id && <span className="tag-mono">Ad group {result.ad_group_id}</span>}
          </div>
        </div>
      )}

      {/* Governance approved it and the platform refused. A different failure
          from a block, and the buyer needs to be able to tell them apart. */}
      {result.outcome === 'APPROVED' && result.created === false && (
        <div className="dec-reason bad">
          <Icon name="alert" size={15} strokeWidth={2.2} />
          <span>{result.platform_error || result.meta_error || `${platformName} did not accept the campaign.`}</span>
        </div>
      )}

      {result.action_id && (
        <div className="dec-foot">
          <span className="tag-mono" style={{ marginLeft: 'auto', background: 'none', padding: 0 }}>
            {result.action_id}
          </span>
        </div>
      )}
    </div>
  );
}

export default function CampaignBuild() {
  const { selectedId, selected, isAll, accountId, platform, platforms } = useClient();
  const google = platform === 'google';
  const platformName = PLATFORM_LABEL[platform] || 'the ad platform';

  // One thread per client AND platform once there are two. History is sent to
  // the model, and a Meta conversation is the wrong context for the Google
  // assistant. A one-platform client keeps the key it always had, so its
  // saved thread is still there.
  const threadKey = platform && platforms.length > 1 ? `${selectedId}.${platform}` : selectedId;

  const [messages, setMessages] = useState(() => loadThread(threadKey));
  const [input, setInput] = useState('');
  const [busy, setBusy] = useState(false);
  const [objective, setObjective] = useState('Leads');
  const [creative, setCreative] = useState(null);
  const [assets, setAssets] = useState([]);
  const [activeRules, setActiveRules] = useState(null);

  const promptRef = useRef(null);
  const endRef = useRef(null);
  const [focused, setFocused] = useState(false);

  // Switching client loads that client's thread rather than clearing it.
  useEffect(() => {
    setMessages(loadThread(threadKey));
    setCreative(null);
  }, [threadKey]);

  useEffect(() => { saveThread(threadKey, messages); }, [threadKey, messages]);

  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
  }, [messages, busy]);

  // Scope data for the chips. Neither is essential to building a campaign, so
  // a failure here is quiet — the page still works, the chip just says less.
  useEffect(() => {
    if (!accountId) { setAssets([]); setActiveRules(null); return; }
    let cancelled = false;

    // Creative is a Meta concept here; a Search ad is text.
    if (google) {
      setAssets([]);
    } else {
      apiGet(`/creative-library/${accountId}`)
        .then((data) => { if (!cancelled) setAssets(data.assets || []); })
        .catch(() => { if (!cancelled) setAssets([]); });
    }

    apiGet(`/rules/${accountId}`)
      .then((data) => {
        if (cancelled) return;
        const rules = data.rules || [];
        setActiveRules(rules.filter((r) => r.is_active).length);
      })
      .catch(() => { if (!cancelled) setActiveRules(null); });

    return () => { cancelled = true; };
  }, [accountId, google]);

  const grow = useCallback(() => {
    const el = promptRef.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = `${el.scrollHeight}px`;
  }, []);

  useEffect(() => { grow(); }, [input, grow]);

  const startNew = () => {
    setMessages([]);
    setInput('');
    try { localStorage.removeItem(storageKey(threadKey)); } catch { /* nothing to clean up */ }
  };

  /**
   * Which objective the person named in their own words, if any.
   *
   * THE CHIP MUST NOT ARGUE WITH THE SENTENCE. "build an awareness campaign"
   * with the objective chip on its default sent
   *
   *     build an awareness campaign ... Objective: Leads.
   *
   * and the agent did the only sensible thing with it: refused to guess and
   * asked which was meant. From the composer that reads as the page rejecting
   * plain English, and the word it objected to is one the person never typed.
   *
   * The old guard only suppressed the append when the text mentioned the
   * SELECTED objective. It never looked for a different one, so every chip
   * value except the matching one produced a message that argued with itself.
   */
  const objectiveNamedIn = (text) => {
    const lower = text.toLowerCase();
    return OBJECTIVES.find((o) => lower.includes(o.toLowerCase())) || null;
  };

  /** Likewise for creative: an asset the person named themselves. */
  const assetNamedIn = (text) => {
    const lower = text.toLowerCase();
    return assets.find(
      (a) => a.asset_name && lower.includes(String(a.asset_name).toLowerCase())
    ) || null;
  };

  /**
   * Fold the chip selections into the message, but only where the person has
   * not already said it themselves. What gets sent is what appears in the
   * thread — the chips are a shortcut for typing, not a hidden payload.
   *
   * Typed words win. The chips carry defaults nobody necessarily chose, and a
   * default silently overriding a sentence someone wrote is the worst way for
   * those two to disagree.
   */
  const composeMessage = (text) => {
    // "What creative do I have?" is a lookup, not a build. Appending an
    // objective and a creative to it is noise at best and misleading at worst.
    if (/^(what|which|why|how|when|who|show|list|can|does|do|is|are)\b/i.test(text.trim())) {
      return text.trim();
    }
    const parts = [text.trim()];
    // The chips are Meta's. On Google they are not shown, and must not add
    // anything either - "Objective: Leads" was being appended to Search
    // requests, where it means nothing.
    if (google) return parts[0];
    if (objective && !objectiveNamedIn(text)) {
      parts.push(`Objective: ${objective}.`);
    }
    if (creative && !assetNamedIn(text)) {
      parts.push(`Use the creative "${creative}".`);
    }
    return parts.join(' ');
  };

  async function submit(question) {
    const raw = (question ?? input).trim();
    if (!raw || busy || isAll) return;

    const message = composeMessage(raw);

    // THE CHIPS FOLLOW THE SENTENCE.
    //
    // Suppressing the contradiction is half of it. Leaving the chip reading
    // "Leads" while an awareness build goes off is the same confusion one
    // step later: the controls would be describing a campaign nobody asked
    // for. They move to what was actually sent, where the person can see it
    // and change it back if the match was wrong.
    const namedObjective = objectiveNamedIn(raw);
    if (namedObjective && namedObjective !== objective) setObjective(namedObjective);
    const namedAsset = assetNamedIn(raw);
    if (namedAsset && namedAsset.asset_name !== creative) setCreative(namedAsset.asset_name);

    setInput('');
    setMessages((m) => [...m, { role: 'user', text: message }]);
    setBusy(true);

    try {
      const history = messages
        .filter((m) => m.role === 'user' || m.role === 'agent')
        .slice(-6)
        .map((m) => ({ role: m.role, text: m.role === 'user' ? m.text : m.reply }));

      const data = await apiPost('/agent/chat', {
        message,
        history,
        client_id: selectedId,
        // Named explicitly: with two platforms connected the server refuses
        // to guess which account to build on.
        platform: platform || undefined,
      });

      setMessages((m) => [...m, { role: 'agent', reply: data.reply, tools: data.tools }]);
    } catch (err) {
      setMessages((m) => [...m, {
        role: 'error',
        text: err instanceof AuthError ? err.message : (err.message || 'That did not work.'),
      }]);
    } finally {
      setBusy(false);
    }
  }

  if (isAll) {
    return (
      <>
        <div className="page-header">
          <h1 className="page-title">Campaign Build</h1>
          <p className="page-subtitle">Campaigns are built for one client at a time</p>
        </div>
        <div className="card">
          <div className="empty-state">
            <h3>Select a client</h3>
            <p>
              Choose a client from the switcher above. The assistant works within that
              client&apos;s ad account and governance rules.
            </p>
          </div>
        </div>
      </>
    );
  }

  const approved = assets.filter((a) => a.is_approved);
  const creativeOptions = assets.map((a) => ({
    value: a.asset_name,
    label: a.asset_name,
    sub: a.format || a.campaign_type || undefined,
    flag: a.is_approved
      ? { cls: 'approved', text: 'Approved' }
      : { cls: 'flagged', text: 'Pending' },
  }));
  const creativeIsPending =
    creative != null && !approved.some((a) => a.asset_name === creative);

  return (
    <>
      <div className="page-header split">
        <div>
          <h1 className="page-title">Campaign Build</h1>
          <p className="page-subtitle">
            Describe the campaign you want for {selected?.name || 'this client'}. Every action is
            checked against their {platformName} rules before anything reaches {platformName}, and
            campaigns are always created paused.
          </p>
        </div>
        <div className="hdr-actions">
          <PlatformSwitch lockedReason={busy ? 'Wait for the current build to finish' : null} />
          {messages.length > 0 && (
            <button className="btn btn-secondary" onClick={startNew} disabled={busy}>
              <Icon name="plus" size={14} strokeWidth={2.4} /> New campaign
            </button>
          )}
        </div>
      </div>

      {/* ── The composer ── */}
      <div className={`composer ${focused ? 'focused' : ''} ${busy ? 'busy' : ''}`}>
        <div className="composer-top">
          <Icon name="spark" size={20} strokeWidth={1.8} className="composer-spark" />
          <textarea
            ref={promptRef}
            rows={1}
            className="composer-input"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onFocus={() => setFocused(true)}
            onBlur={() => setFocused(false)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); submit(); }
            }}
            placeholder={google
              ? 'Describe the campaign — budget, bid, keywords, landing page, countries…'
              : 'Describe the campaign — objective, budget, audience, creative…'}
            disabled={busy}
            aria-label="Describe the campaign you want to build"
          />
        </div>

        <div className="composer-controls">
          <Chip icon="user" label={selected?.name || 'This client'} />
          {!google && <Chip
            icon="target"
            label={objective}
            value={objective}
            options={OBJECTIVES.map((o) => ({ value: o, label: o }))}
            onPick={setObjective}
            disabled={busy}
          />}
          {!google && <Chip
            icon="image"
            label={creative || 'Any approved creative'}
            value={creative}
            warn={creativeIsPending}
            options={[{ value: null, label: 'Any approved creative' }, ...creativeOptions]}
            onPick={setCreative}
            disabled={busy || assets.length === 0}
          />}

          <span className="ctrl-spacer" />

          {activeRules != null && (
            <span className="rulecount">
              <Icon name="shield" size={13} /> {activeRules} rules active
            </span>
          )}

          <button
            className="btn-build"
            onClick={() => submit()}
            disabled={busy || !input.trim()}
          >
            {busy ? 'Building…' : 'Build'}
            <Icon name="right" size={14} strokeWidth={2.6} />
          </button>
        </div>
      </div>

      {/* ── Starting points ── */}
      {messages.length === 0 && !busy && (
        <>
          <div className="suggestions">
            {(google ? QUICK_GOOGLE : QUICK).map((s) => (
              <button key={s.text} className="sug" onClick={() => submit(s.text)}>
                <i style={{ background: s.colour }} />
                {s.text}
              </button>
            ))}
          </div>

          {!google && <div className="section-lede">Or start from a goal</div>}
          {!google && <div className="topics">
            {TOPICS.map((t) => (
              <button
                key={t.title}
                className="topic"
                onClick={() => {
                  setInput(`Build a ${t.title.toLowerCase()} campaign at £200 a day`);
                  promptRef.current?.focus();
                }}
              >
                <div className="topic-h">
                  {t.title}
                  <Icon name="chevron" size={14} strokeWidth={2.4} />
                </div>
                <p>{t.blurb}</p>
              </button>
            ))}
          </div>}
        </>
      )}

      {/* ── The thread ── */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-6)', marginTop: 'var(--space-6)' }}>
        {messages.map((m, i) => {
          if (m.role === 'user') {
            return <div key={i} className="rp-msg-user">{m.text}</div>;
          }
          if (m.role === 'error') {
            return <div key={i} className="rp-msg-error">{m.text}</div>;
          }
          // The LAST build call: after a proposal is sent back to fix, the
          // assistant calls again, and the outcome is the second call's.
          const decision = [...(m.tools || [])].reverse().find((t) => BUILD_TOOLS.includes(t.tool));
          return (
            <div key={i}>
              <AgentSteps tools={m.tools} />
              {decision && <DecisionCard result={decision.result} args={decision.args} />}
              {m.reply && (
                <div className="rp-answer" style={{ marginTop: 'var(--space-4)' }}>{m.reply}</div>
              )}
            </div>
          );
        })}

        {busy && <AgentSteps busy />}
        <div ref={endRef} />
      </div>

      <p className="rp-disclaimer">
        Campaigns are created paused and must be activated in {ACTIVATE_IN[platform] || 'the ad platform'}. Actions a rule flags
        are held in the approval queue and are not created.
      </p>
    </>
  );
}